'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import AlertConfigTab from './AlertConfigTab';

// ─── Types ────────────────────────────────────────────────────────────────────
interface InstallerCrew {
  id: string;
  company_name: string;
  foreman_name: string;
  num_crews: number;
  crew_size: string;
  customer_rating: number;
  quality_rating: string;
  compliance_status: string;
  compliance_issues: any[];
  performance_metrics: { on_time_arrival: number; on_time_completion: number; cost_accuracy: number };
  documents: { name: string; upload_date: string | null; expiration_date: string | null; status: string }[];
  onboarding_step: number | null;
  state?: string;
}

interface StateMetric {
  state: string;
  activeCrews: number;
  avgQualityRating: number;
  avgCustomerRating: number;
  totalCapacity: number;
}

interface WeeklyTrend {
  week: string;
  onTimeArrival: number;
  onTimeCompletion: number;
  atCostCompletion: number;
}

type TabKey = 'byState' | 'performance' | 'compliance' | 'networkHealth' | 'alertConfig';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'byState', label: 'By State' },
  { key: 'performance', label: 'Performance Trends' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'networkHealth', label: 'Network Health' },
  { key: 'alertConfig', label: 'Alert Config' },
];

const QUALITY_SCORE: Record<string, number> = {
  'A+': 4.3, 'A': 4.0, 'A-': 3.7, 'B+': 3.3, 'B': 3.0, 'B-': 2.7,
  'C+': 2.3, 'C': 2.0, 'N/A': 0,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function qualityToScore(q: string): number {
  return QUALITY_SCORE[q] ?? 0;
}

function buildStateMetrics(crews: InstallerCrew[]): StateMetric[] {
  const map: Record<string, InstallerCrew[]> = {};
  crews.forEach(c => {
    const st = c.state || 'Unknown';
    if (!map[st]) map[st] = [];
    map[st].push(c);
  });
  return Object.entries(map).map(([state, list]) => {
    const active = list.filter(c => c.compliance_status !== 'onboarding');
    const avgQ = active.length
      ? active.reduce((s, c) => s + qualityToScore(c.quality_rating), 0) / active.length
      : 0;
    const avgC = active.length
      ? active.reduce((s, c) => s + c.customer_rating, 0) / active.length
      : 0;
    const totalCap = list.reduce((s, c) => s + c.num_crews, 0);
    return {
      state,
      activeCrews: active.length,
      avgQualityRating: parseFloat(avgQ.toFixed(2)),
      avgCustomerRating: parseFloat(avgC.toFixed(2)),
      totalCapacity: totalCap,
    };
  }).sort((a, b) => b.activeCrews - a.activeCrews);
}

function buildWeeklyTrends(crews: InstallerCrew[]): WeeklyTrend[] {
  const active = crews.filter(c => c.compliance_status !== 'onboarding' && c.performance_metrics);
  const avgArrival = active.length
    ? active.reduce((s, c) => s + (c.performance_metrics?.on_time_arrival ?? 0), 0) / active.length
    : 0;
  const avgCompletion = active.length
    ? active.reduce((s, c) => s + (c.performance_metrics?.on_time_completion ?? 0), 0) / active.length
    : 0;
  const avgCost = active.length
    ? active.reduce((s, c) => s + (c.performance_metrics?.cost_accuracy ?? 0), 0) / active.length
    : 0;

  // Generate 8 weeks of simulated trend data around the actual averages
  const weeks: WeeklyTrend[] = [];
  const now = new Date();
  for (let i = 7; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i * 7);
    const label = `W${8 - i} (${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
    const variance = () => (Math.random() - 0.5) * 6;
    weeks.push({
      week: label,
      onTimeArrival: Math.min(100, Math.max(60, parseFloat((avgArrival + variance()).toFixed(1)))),
      onTimeCompletion: Math.min(100, Math.max(60, parseFloat((avgCompletion + variance()).toFixed(1)))),
      atCostCompletion: Math.min(100, Math.max(70, parseFloat((avgCost + variance()).toFixed(1)))),
    });
  }
  return weeks;
}

function exportCSV(data: Record<string, any>[], filename: string) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(row => headers.map(h => JSON.stringify(row[h] ?? '')).join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-800 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }} className="font-medium">
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}
          {p.unit || ''}
        </p>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ReportsAnalyticsInteractive() {
  const { hasAnyRole } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<TabKey>('byState');
  const [crews, setCrews] = useState<InstallerCrew[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('Last 8 Weeks');
  const [showDatePicker, setShowDatePicker] = useState(false);

  const canAccess = hasAnyRole(['Admin', 'Manager']);

  // ── Fetch crews ──────────────────────────────────────────────────────────────
  const fetchCrews = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('installer_crews')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error && data) setCrews(data as InstallerCrew[]);
    } catch {
      // silently handle
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchCrews();
    const channel = supabase
      .channel('reports-crews-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'installer_crews' }, fetchCrews)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchCrews, supabase]);

  // ── Derived data ─────────────────────────────────────────────────────────────
  const stateMetrics = buildStateMetrics(crews);
  const weeklyTrends = buildWeeklyTrends(crews);

  const totalActive = crews.filter(c => c.compliance_status !== 'onboarding').length;
  const totalOnboarding = crews.filter(c => c.compliance_status === 'onboarding').length;
  const complianceWarnings = crews.filter(c => c.compliance_status === 'action_needed').length;
  const networkCapacity = crews.reduce((s, c) => s + c.num_crews, 0);

  const compliantCrews = crews.filter(c => c.compliance_status === 'compliant').length;
  const expiredDocs = crews.filter(c =>
    (c.documents || []).some(d => d.status === 'expired')
  ).length;

  // ── Export handler ────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (activeTab === 'byState') {
      exportCSV(stateMetrics, 'crew-metrics-by-state.csv');
    } else if (activeTab === 'performance') {
      exportCSV(weeklyTrends, 'performance-trends.csv');
    } else if (activeTab === 'compliance') {
      const rows = crews.map(c => ({
        company: c.company_name,
        foreman: c.foreman_name,
        status: c.compliance_status,
        issues: (c.compliance_issues || []).map((i: any) => i.type).join('; '),
        expiredDocs: (c.documents || []).filter(d => d.status === 'expired').map(d => d.name).join('; '),
      }));
      exportCSV(rows, 'compliance-status.csv');
    } else {
      exportCSV([{ totalActive, totalOnboarding, complianceWarnings, networkCapacity }], 'network-health.csv');
    }
  };

  // ── Access guard ──────────────────────────────────────────────────────────────
  if (!canAccess) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Icon name="LockClosedIcon" size={28} className="text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Access Restricted</h2>
          <p className="text-sm text-gray-500">This section is available to SuperAdmin, Executive, and Operations roles.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Sticky Header ──────────────────────────────────────────────────────── */}
      <div className="sticky top-[60px] z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
              <Icon name="ChartBarIcon" size={16} className="text-white" variant="solid" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 font-heading">Reports &amp; Analytics</h1>
            {loading && (
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full animate-pulse">Syncing…</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Date Range Picker */}
            <div className="relative">
              <button
                onClick={() => setShowDatePicker(p => !p)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-all"
              >
                <Icon name="CalendarDaysIcon" size={16} className="text-gray-400" />
                <span className="hidden sm:inline">{dateRange}</span>
                <Icon name="ChevronDownIcon" size={14} className="text-gray-400" />
              </button>
              {showDatePicker && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-2 min-w-[180px]">
                  {['Last 4 Weeks', 'Last 8 Weeks', 'Last 12 Weeks', 'Last 6 Months', 'This Year'].map(opt => (
                    <button
                      key={opt}
                      onClick={() => { setDateRange(opt); setShowDatePicker(false); }}
                      className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all ${dateRange === opt ? 'bg-cyan-50 text-cyan-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Messenger */}
            <button className="relative p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all">
              <Icon name="ChatBubbleLeftRightIcon" size={20} variant="outline" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-cyan-500 rounded-full" />
            </button>

            {/* Export Button */}
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all hover:opacity-90 active:scale-95 shadow-sm"
              style={{ background: '#E89A3C' }}
            >
              <Icon name="ArrowDownTrayIcon" size={16} className="text-white" variant="solid" />
              <span className="hidden sm:inline">Export Dashboard</span>
              <span className="sm:hidden">Export</span>
            </button>
          </div>
        </div>

        {/* ── Tab Bar ──────────────────────────────────────────────────────────── */}
        <div className="flex gap-0 px-6 border-t border-gray-100">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-cyan-500 text-cyan-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Main Content ───────────────────────────────────────────────────────── */}
      <div className="p-6 space-y-6">

        {/* ══ BY STATE ══════════════════════════════════════════════════════════ */}
        {activeTab === 'byState' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Crew Metrics by State</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Active crew counts, quality ratings, and capacity per state</p>
                </div>
                <span className="text-xs text-gray-400 bg-gray-50 px-2.5 py-1 rounded-full border border-gray-200">
                  {stateMetrics.length} states
                </span>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : stateMetrics.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Icon name="MapIcon" size={40} className="mb-3 opacity-40" />
                  <p className="text-sm">No state data available</p>
                </div>
              ) : (
                <>
                  {/* Bar Chart */}
                  <div className="px-6 pt-4 pb-2">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={stateMetrics} margin={{ top: 4, right: 16, left: -10, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="state" tick={{ fontSize: 12, fill: '#6b7280' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="activeCrews" name="Active Crews" fill="#E89A3C" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="totalCapacity" name="Total Capacity" fill="#1C2E5E" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Data Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-t border-gray-100">
                          <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">State</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Active Crews</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg Quality</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg Customer Rating</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Capacity</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {stateMetrics.map((row, i) => (
                          <tr key={row.state} className={`hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/40'}`}>
                            <td className="px-6 py-3.5 font-medium text-gray-900">{row.state}</td>
                            <td className="px-6 py-3.5 text-right">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold text-white" style={{ background: '#E89A3C' }}>
                                {row.activeCrews}
                              </span>
                            </td>
                            <td className="px-6 py-3.5 text-right text-gray-700">{row.avgQualityRating.toFixed(2)}</td>
                            <td className="px-6 py-3.5 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <div className="flex gap-0.5">
                                  {[1,2,3,4,5].map(s => (
                                    <svg key={s} className={`w-3 h-3 ${s <= Math.round(row.avgCustomerRating) ? 'text-amber-400' : 'text-gray-200'}`} fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                  ))}
                                </div>
                                <span className="text-gray-600 text-xs">{row.avgCustomerRating.toFixed(1)}</span>
                              </div>
                            </td>
                            <td className="px-6 py-3.5 text-right font-medium text-gray-700">{row.totalCapacity} crews</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ══ PERFORMANCE TRENDS ════════════════════════════════════════════════ */}
        {activeTab === 'performance' && (
          <div className="space-y-6">
            {/* KPI Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: 'Avg On-Time Arrival', value: crews.filter(c => c.compliance_status !== 'onboarding').length ? (crews.filter(c => c.compliance_status !== 'onboarding').reduce((s, c) => s + (c.performance_metrics?.on_time_arrival ?? 0), 0) / crews.filter(c => c.compliance_status !== 'onboarding').length).toFixed(1) : '—', unit: '%', color: '#E89A3C', icon: 'ClockIcon' },
                { label: 'Avg On-Time Completion', value: crews.filter(c => c.compliance_status !== 'onboarding').length ? (crews.filter(c => c.compliance_status !== 'onboarding').reduce((s, c) => s + (c.performance_metrics?.on_time_completion ?? 0), 0) / crews.filter(c => c.compliance_status !== 'onboarding').length).toFixed(1) : '—', unit: '%', color: '#1C2E5E', icon: 'CheckCircleIcon' },
                { label: 'Avg At-Cost Completion', value: crews.filter(c => c.compliance_status !== 'onboarding').length ? (crews.filter(c => c.compliance_status !== 'onboarding').reduce((s, c) => s + (c.performance_metrics?.cost_accuracy ?? 0), 0) / crews.filter(c => c.compliance_status !== 'onboarding').length).toFixed(1) : '—', unit: '%', color: '#10b981', icon: 'CurrencyDollarIcon' },
              ].map(kpi => (
                <div key={kpi.label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${kpi.color}18` }}>
                    <Icon name={kpi.icon as any} size={22} style={{ color: kpi.color }} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">{kpi.label}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-0.5">{kpi.value}<span className="text-sm font-normal text-gray-400 ml-0.5">{kpi.unit}</span></p>
                  </div>
                </div>
              ))}
            </div>

            {/* Line Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">Week-over-Week Performance Trends</h2>
                <p className="text-xs text-gray-500 mt-0.5">8-week rolling averages across all active crews</p>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="px-6 py-4">
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={weeklyTrends} margin={{ top: 4, right: 16, left: -10, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#6b7280' }} interval={0} angle={-20} textAnchor="end" height={50} />
                      <YAxis domain={[60, 100]} tick={{ fontSize: 12, fill: '#6b7280' }} unit="%" />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="onTimeArrival" name="On-Time Arrival %" stroke="#E89A3C" strokeWidth={2.5} dot={{ r: 4, fill: '#E89A3C' }} activeDot={{ r: 6 }} unit="%" />
                      <Line type="monotone" dataKey="onTimeCompletion" name="On-Time Completion %" stroke="#1C2E5E" strokeWidth={2.5} dot={{ r: 4, fill: '#1C2E5E' }} activeDot={{ r: 6 }} unit="%" />
                      <Line type="monotone" dataKey="atCostCompletion" name="At-Cost Completion %" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4, fill: '#10b981' }} activeDot={{ r: 6 }} unit="%" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Per-Crew Performance Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">Per-Crew Performance Breakdown</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Crew</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">On-Time Arrival</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">On-Time Completion</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">At-Cost Completion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {crews.filter(c => c.compliance_status !== 'onboarding').map(crew => {
                      const pm = crew.performance_metrics || { on_time_arrival: 0, on_time_completion: 0, cost_accuracy: 0 };
                      const bar = (val: number, color: string) => (
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${val}%`, background: color }} />
                          </div>
                          <span className="text-gray-700 w-10 text-right">{val}%</span>
                        </div>
                      );
                      return (
                        <tr key={crew.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-3.5">
                            <p className="font-medium text-gray-900">{crew.company_name}</p>
                            <p className="text-xs text-gray-400">{crew.foreman_name}</p>
                          </td>
                          <td className="px-6 py-3.5">{bar(pm.on_time_arrival, '#E89A3C')}</td>
                          <td className="px-6 py-3.5">{bar(pm.on_time_completion, '#1C2E5E')}</td>
                          <td className="px-6 py-3.5">{bar(pm.cost_accuracy, '#10b981')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ══ COMPLIANCE ════════════════════════════════════════════════════════ */}
        {activeTab === 'compliance' && (
          <div className="space-y-6">
            {/* Summary KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: 'Compliant Crews', value: compliantCrews, icon: 'ShieldCheckIcon', color: '#10b981', bg: '#10b98118', desc: 'All documents valid' },
                { label: 'Compliance Warnings', value: complianceWarnings, icon: 'ExclamationTriangleIcon', color: '#f59e0b', bg: '#f59e0b18', desc: 'Action required' },
                { label: 'Expired Documents', value: expiredDocs, icon: 'XCircleIcon', color: '#ef4444', bg: '#ef444418', desc: 'Crews with expired docs' },
              ].map(kpi => (
                <div key={kpi.label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: kpi.bg }}>
                    <Icon name={kpi.icon as any} size={22} style={{ color: kpi.color }} variant="solid" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">{kpi.label}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-0.5">{kpi.value}</p>
                    <p className="text-xs text-gray-400">{kpi.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Compliance Detail Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Compliance Status Overview</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Document expiry tracking for all crews</p>
                </div>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Crew</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">General Liability</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Workers Comp</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">W9</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Issues</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {crews.map(crew => {
                        const isCompliant = crew.compliance_status === 'compliant';
                        const isOnboarding = crew.compliance_status === 'onboarding';
                        const isWarning = crew.compliance_status === 'action_needed';

                        const docMap: Record<string, { expiration_date: string | null; status: string }> = {};
                        (crew.documents || []).forEach(d => { docMap[d.name] = d; });

                        const docCell = (docName: string) => {
                          const doc = docMap[docName];
                          if (!doc) return <span className="text-gray-300 text-xs">—</span>;
                          const isExpired = doc.status === 'expired';
                          const isPending = doc.status === 'pending';
                          return (
                            <div className="flex flex-col gap-0.5">
                              <span className={`text-xs font-medium ${isExpired ? 'text-red-600' : isPending ? 'text-amber-500' : 'text-emerald-600'}`}>
                                {isExpired ? '✗ Expired' : isPending ? '⏳ Pending' : '✓ Valid'}
                              </span>
                              {doc.expiration_date && (
                                <span className="text-[11px] text-gray-400">Exp: {new Date(doc.expiration_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</span>
                              )}
                            </div>
                          );
                        };

                        return (
                          <tr key={crew.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <p className="font-medium text-gray-900">{crew.company_name}</p>
                              <p className="text-xs text-gray-400">{crew.foreman_name}</p>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                isCompliant ? 'bg-emerald-50 text-emerald-700' : isWarning ?'bg-red-50 text-red-700': isOnboarding ?'bg-blue-50 text-blue-700': 'bg-gray-100 text-gray-600'
                              }`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${isCompliant ? 'bg-emerald-500' : isWarning ? 'bg-red-500' : 'bg-blue-400'}`} />
                                {isCompliant ? 'Compliant' : isWarning ? 'Action Needed' : isOnboarding ? 'Onboarding' : crew.compliance_status}
                              </span>
                            </td>
                            <td className="px-4 py-4">{docCell('General Liability')}</td>
                            <td className="px-4 py-4">{docCell('Workers Comp')}</td>
                            <td className="px-4 py-4">{docCell('W9')}</td>
                            <td className="px-4 py-4">
                              {(crew.compliance_issues || []).length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {(crew.compliance_issues as any[]).map((issue, idx) => (
                                    <span key={idx} className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full inline-block w-fit">
                                      {issue.type}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-300">None</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ NETWORK HEALTH ════════════════════════════════════════════════════ */}
        {activeTab === 'networkHealth' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total Active Crews', value: totalActive, icon: 'WrenchScrewdriverIcon', color: '#E89A3C', bg: '#E89A3C18', trend: '+2 this month', trendUp: true },
                { label: 'Total Onboarding', value: totalOnboarding, icon: 'UserPlusIcon', color: '#8b5cf6', bg: '#8b5cf618', trend: 'In progress', trendUp: null },
                { label: 'Compliance Warnings', value: complianceWarnings, icon: 'ExclamationTriangleIcon', color: '#f59e0b', bg: '#f59e0b18', trend: complianceWarnings > 0 ? 'Needs attention' : 'All clear', trendUp: complianceWarnings === 0 },
                { label: 'Network Capacity', value: networkCapacity, icon: 'ChartBarIcon', color: '#1C2E5E', bg: '#1C2E5E18', trend: 'Total crew units', trendUp: null },
              ].map(kpi => (
                <div key={kpi.label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: kpi.bg }}>
                      <Icon name={kpi.icon as any} size={20} style={{ color: kpi.color }} variant="solid" />
                    </div>
                    {kpi.trendUp !== null && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${kpi.trendUp ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                        {kpi.trendUp ? '↑' : '↓'} {kpi.trend}
                      </span>
                    )}
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{loading ? '—' : kpi.value}</p>
                  <p className="text-sm text-gray-500 mt-1">{kpi.label}</p>
                  {kpi.trendUp === null && <p className="text-xs text-gray-400 mt-0.5">{kpi.trend}</p>}
                </div>
              ))}
            </div>

            {/* Network Health Bar Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">Network Composition</h2>
                <p className="text-xs text-gray-500 mt-0.5">Breakdown of crew status across the network</p>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="px-6 py-4">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={[
                        { name: 'Compliant', count: compliantCrews, fill: '#10b981' },
                        { name: 'Action Needed', count: complianceWarnings, fill: '#f59e0b' },
                        { name: 'Onboarding', count: totalOnboarding, fill: '#8b5cf6' },
                      ]}
                      margin={{ top: 4, right: 16, left: -10, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 13, fill: '#6b7280' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Crews" radius={[6, 6, 0, 0]}>
                        {[
                          { fill: '#10b981' },
                          { fill: '#f59e0b' },
                          { fill: '#8b5cf6' },
                        ].map((entry, index) => (
                          <rect key={index} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Crew Roster Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">Full Network Roster</h2>
                <p className="text-xs text-gray-500 mt-0.5">All crews with status and capacity overview</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Foreman</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Crews</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Crew Size</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer Rating</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {crews.map(crew => (
                      <tr key={crew.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-3.5 font-medium text-gray-900">{crew.company_name}</td>
                        <td className="px-4 py-3.5 text-gray-600">{crew.foreman_name}</td>
                        <td className="px-4 py-3.5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                            crew.compliance_status === 'compliant' ? 'bg-emerald-50 text-emerald-700' :
                            crew.compliance_status === 'action_needed' ? 'bg-red-50 text-red-700' :
                            crew.compliance_status === 'onboarding'? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              crew.compliance_status === 'compliant' ? 'bg-emerald-500' :
                              crew.compliance_status === 'action_needed'? 'bg-red-500' : 'bg-blue-400'
                            }`} />
                            {crew.compliance_status === 'compliant' ? 'Compliant' :
                             crew.compliance_status === 'action_needed' ? 'Action Needed' :
                             crew.compliance_status === 'onboarding' ? 'Onboarding' :
                             crew.compliance_status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-right font-medium text-gray-700">{crew.num_crews}</td>
                        <td className="px-4 py-3.5 text-right text-gray-500">{crew.crew_size}</td>
                        <td className="px-6 py-3.5 text-right">
                          {crew.customer_rating > 0 ? (
                            <span className="font-medium text-gray-700">{crew.customer_rating.toFixed(1)} ★</span>
                          ) : (
                            <span className="text-gray-300 text-xs">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ══ ALERT CONFIG ══════════════════════════════════════════════════════ */}
        {activeTab === 'alertConfig' && (
          <AlertConfigTab />
        )}
      </div>
    </div>
  );
}
